<template>
    <SearchWorkshopReadyOrders @send-data="handleOrderSelection" @close="close" />
</template>

<script>
import axios from "@/api";
import { errorActions } from "@/alerts";
import SearchWorkshopReadyOrders from "@/components/Sales/SearchWorkshopReadyOrders.vue";

export default {
    name: "WorkshopOrderSaleLoader",
    components: {
        SearchWorkshopReadyOrders,
    },
    emits: ["send-data", "close"],
    methods: {
        buildWorkshopEstimatedAmount(order) {
            if (order?.presupuesto !== null && order?.presupuesto !== undefined) {
                return Number(order.presupuesto || 0);
            }

            const details = Array.isArray(order?.details) ? order.details : [];
            const detailsTotal = details.reduce((accumulator, detail) => {
                const quantity = Number(detail?.cantidad || 0);
                const unitPrice = Number(detail?.precio_unitario || 0);
                return accumulator + (Number.isFinite(quantity) && Number.isFinite(unitPrice)
                    ? Math.max(quantity, 0) * Math.max(unitPrice, 0)
                    : 0);
            }, 0);

            if (detailsTotal > 0) {
                return detailsTotal;
            }

            if (
                order?.precio_articulo_referencia !== null &&
                order?.precio_articulo_referencia !== undefined
            ) {
                return Number(order.precio_articulo_referencia || 0);
            }

            return Number(order?.precio_chequeo || 0);
        },
        buildWorkshopPendingAmount(order) {
            return Math.max(
                this.buildWorkshopEstimatedAmount(order) -
                Number(order?.monto_pagado_total || 0),
                0
            );
        },
        mapCustomerToSearchPayload(customer) {
            return {
                idCustomer: customer?.id,
                maximumDiscount: parseFloat(customer?.descuento_limite_asignado || 0),
                creditLimit: parseFloat(customer?.limite_de_credito || 0),
                typeCustomer: customer?.tipo_de_cliente,
                numberDocument: String(customer?.numero_documento || ""),
                sell_with: customer?.sell_with || 1,
                fixed_voucher: customer?.fixed_voucher || 0,
                searchInput: customer?.nombres || "",
                requiere_codigo_pedido: Boolean(customer?.requiere_codigo_pedido),
                paydays: parseInt(customer?.paydays || 0),
            };
        },
        createWorkshopOrderLine(order) {
            const details = Array.isArray(order?.details) ? order.details : [];
            const pendingAmount = this.buildWorkshopPendingAmount(order);
            const saleLines = [];

            if (!details.length && order?.idproducto_referencia) {
                const referenceProduct = order?.productReference || {};
                saleLines.push(this.createSaleLine(order, {
                    idproducto: order.idproducto_referencia,
                    descripcion: order.articulo_referencia || order.productReference?.nombre_producto || `Servicio de taller ${order.codigo || order.id}`,
                    taxFree: referenceProduct?.libre_de_impuesto ?? 0,
                    igvApplied: Number(referenceProduct?.igv || 0),
                    tipo: Number(referenceProduct?.tipo || 0),
                    subtotal: pendingAmount,
                    id: `legacy-${order.id}`,
                }, pendingAmount));

                return saleLines;
            }

            let remainingPending = pendingAmount;

            details.forEach((detail) => {
                const quantity = Number(detail?.cantidad || 0);
                const unitPrice = Number(detail?.precio_unitario || 0);
                const lineTotal = (Number.isFinite(quantity) && Number.isFinite(unitPrice))
                    ? Math.max(quantity, 0) * Math.max(unitPrice, 0)
                    : 0;
                const amountToCharge = Math.min(lineTotal, remainingPending);

                if (amountToCharge <= 0) {
                    return;
                }

                saleLines.push(this.createSaleLine(order, {
                    ...detail,
                    taxFree: detail?.product?.libre_de_impuesto ?? 0,
                    igvApplied: Number(detail?.product?.igv || 0),
                    tipo: Number(detail?.product?.tipo || 0),
                }, amountToCharge));
                remainingPending = Math.max(remainingPending - amountToCharge, 0);
            });

            return saleLines;
        },
        createSaleLine(order, detail, amountToCharge) {
            return {
                id: 0,
                discount: 0,
                expired: null,
                idProduct: detail.idproducto,
                igv: 0,
                tipo: Number(detail?.tipo || 0),
                igvApplied: Number(detail?.igvApplied || 0),
                insuranceDiscount: 0,
                maximumDiscount: 0,
                poster: null,
                productName: detail.descripcion || `Servicio de taller ${order.codigo || order.id}`,
                quantity: 1,
                baseSalePrice: amountToCharge,
                salePrice: amountToCharge,
                subTotal: amountToCharge,
                taxFree: Boolean(Number(detail?.taxFree || 0)),
                total: amountToCharge,
                unitMeasure: "UNIDAD",
                units: [
                    {
                        unidad_de_medida: "UNIDAD",
                        precio: amountToCharge,
                        precio_dos: 0,
                        precio_tres: 0,
                        cantidad: 1,
                        factor_stock: 1,
                    },
                ],
                quantityContained: 1,
                precio_descuento: amountToCharge,
                vencimiento_descuento: null,
                productSerie: null,
                warranty_expiration: 0,
                warranty_type: null,
                shoppingPrice: 0,
                showBTNPriceTwo: false,
                showBTNPriceThree: false,
                priceSelectedForTheCustomer: 1,
                promotionDiscountAmount: 0,
                bonusQuantity: 0,
                appliedPromotions: [],
                isWorkshopOrder: true,
                workshopOrderId: order.id,
                workshopOrderCode: order.codigo,
                workshopPendingAmount: amountToCharge,
                workshopDetailId: detail.id || null,
            };
        },
        async handleOrderSelection(order) {
            const details = Array.isArray(order?.details) ? order.details : [];
            const hasBillableDetails = details.some((detail) => Number(detail?.idproducto || 0) > 0);

            if (!hasBillableDetails && !order?.idproducto_referencia) {
                errorActions(
                    "La orden de taller no tiene líneas cobrables con producto/servicio configurado para poder cargarla en ventas."
                );
                return;
            }

            const pendingAmount = this.buildWorkshopPendingAmount(order);
            if (pendingAmount <= 0) {
                errorActions(
                    "La orden seleccionada ya no tiene saldo pendiente para cargar en esta venta."
                );
                return;
            }

            const customerId =
                order?.customer?.id || order?.idcliente || order?.customer_id || 0;
            if (!customerId) {
                errorActions(
                    "No pudimos identificar el cliente de esta orden para cargarlo en la venta."
                );
                return;
            }

            try {
                const response = await axios.get(`/clientes/${customerId}`);
                const customerPayload = this.mapCustomerToSearchPayload(
                    response?.data?.data || response?.data || {}
                );

                if (!customerPayload.idCustomer) {
                    errorActions(
                        "No pudimos cargar el cliente de la orden de taller."
                    );
                    return;
                }

                this.$emit("send-data", {
                    customerPayload,
                    customerName: customerPayload.searchInput,
                    workshopOrder: order,
                    saleLines: this.createWorkshopOrderLine(order),
                });
            } catch (error) {
                errorActions(error);
            }
        },
        close() {
            this.$emit("close");
        },
    },
};
</script>